import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { APIProvider, Map as GoogleMap, Marker, useMap } from '@vis.gl/react-google-maps';
import { ItineraryItem, Day } from '../types';
import { TripPlannerAPI } from '../services/api';
import DateScroller from './DateScroller';

interface MapViewProps {
  items: ItineraryItem[];
  selectedItemId?: string;
  onItemSelect?: (itemId: string) => void;
  centerOnItemId?: string;
  days?: Day[]; // Optional days array to get colors for markers
  selectedDayId?: string | null; // Selected day ID for filtering markers
  onDateSelect?: (dayId: string) => void; // Callback when date is selected
  isMobile?: boolean; // Whether this is mobile view
}

// Component to add custom My Location button
const MyLocationButton: React.FC<{ onLocationFound: (location: { lat: number; lng: number }) => void }> = ({ onLocationFound }) => {
  const map = useMap();
  const [isLocating, setIsLocating] = useState(false);
  
  const handleMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        if (map) {
          map.setCenter(location);
          map.setZoom(15);
        }
        
        // Notify parent component of the location
        onLocationFound(location);
        
        setIsLocating(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsLocating(false);
        alert('Unable to retrieve your location. Please check your browser permissions.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }, [map, onLocationFound]);
  
  return (
    <button
      onClick={handleMyLocation}
      disabled={isLocating}
      className="absolute bottom-4 left-4 z-10 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white p-3 rounded-full shadow-lg transition-colors duration-200 flex items-center justify-center"
      title="Find my location"
    >
      {isLocating ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
    </button>
  );
};

// Inner component that uses the Maps library
const MapContent: React.FC<MapViewProps> = ({ items, selectedItemId, onItemSelect, centerOnItemId, days, selectedDayId, onDateSelect, isMobile }) => {
  const map = useMap();
  
  // State for map centering
  const [isCentering, setIsCentering] = useState(false);
  
  // State for current zoom level
  const [currentZoom, setCurrentZoom] = useState(14);
  
  // State for user's current location
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Ref to track if the last click came from a marker
  const clickFromMarkerRef = useRef(false);
  
  // Create mapping from itemId to dayId and dayId to color
  const { itemToDayMap, dayColorMap } = useMemo(() => {
    const itemToDay: Record<string, string> = {};
    const dayColors: Record<string, string> = {};
    
    if (days) {
      days.forEach(day => {
        // Store day color
        dayColors[day.dayId] = day.color || '#3B82F6'; // Default to blue if no color
        
        // Map each item in the day to the dayId
        day.itinerary.forEach(item => {
          itemToDay[item.itemId] = day.dayId;
        });
      });
    }
    
    return {
      itemToDayMap: itemToDay,
      dayColorMap: dayColors
    };
  }, [days]);

  // Routing polylines state
  const polylinesRef = useRef<Map<string, google.maps.Polyline>>(new Map());
  const prevItemIdsRef = useRef<string[] | null>(null);
  const routesCalculatedRef = useRef(false); // Track if routes have been calculated

  // Color for a leg (from one item to the next)
  const getLegColor = useCallback((fromItemId: string, toItemId: string) => {
    const fromDayId = itemToDayMap[fromItemId];
    const toDayId = itemToDayMap[toItemId];
    const dayId = (fromDayId === toDayId) ? fromDayId : toDayId; // cross-day uses following day color
    return (dayId && dayColorMap[dayId]) ? dayColorMap[dayId] : '#3B82F6';
  }, [itemToDayMap, dayColorMap]);

  // Get the day ID for a leg (used to determine if it should be highlighted)
  const getLegDayId = useCallback((fromItemId: string, toItemId: string) => {
    const fromDayId = itemToDayMap[fromItemId];
    const toDayId = itemToDayMap[toItemId];
    // Cross-day legs use the following day's ID
    return (fromDayId === toDayId) ? fromDayId : toDayId;
  }, [itemToDayMap]);

  // Determine if a leg should be fully visible based on selected item
  const isLegVisible = useCallback((fromItemId: string, toItemId: string) => {
    if (!selectedItemId) return false; // No selection = transparent
    
    const legDayId = getLegDayId(fromItemId, toItemId);
    const selectedDayId = itemToDayMap[selectedItemId];
    
    return legDayId === selectedDayId;
  }, [selectedItemId, itemToDayMap, getLegDayId]);

  // Update all polyline opacities based on selected item
  const updateAllPolylineOpacities = useCallback(() => {
    polylinesRef.current.forEach((polyline, key) => {
      // Extract item IDs from key (format: "itemId1__itemId2")
      const [fromId, toId] = key.split('__');
      if (fromId && toId) {
        const shouldBeVisible = isLegVisible(fromId, toId);
        // 1.0 = fully opaque (0% transparent), 0.15 = mostly transparent (85% transparent)
        const opacity = shouldBeVisible ? 1.0 : 0.15;
        polyline.setOptions({ strokeOpacity: opacity });
      }
    });
  }, [isLegVisible]);

  const pairKey = useCallback((a: string, b: string) => `${a}__${b}`, []);

  const clearAllPolylines = useCallback(() => {
    polylinesRef.current.forEach(poly => poly.setMap(null));
    polylinesRef.current.clear();
  }, []);

  // Draw polyline for a single leg path with color and opacity
  const drawPolylineForLeg = useCallback((path: google.maps.LatLng[] | google.maps.LatLngLiteral[], fromId: string, toId: string, opacity?: number) => {
    if (!map || !path || path.length === 0) return;
    const color = getLegColor(fromId, toId);
    // 1.0 = fully opaque (0% transparent), 0.15 = mostly transparent (85% transparent)
    const defaultOpacity = opacity !== undefined ? opacity : (isLegVisible(fromId, toId) ? 1.0 : 0.15);
    
    // Check if polyline already exists and update it
    const key = pairKey(fromId, toId);
    const existingPolyline = polylinesRef.current.get(key);
    
    if (existingPolyline) {
      // Update existing polyline
      existingPolyline.setOptions({
        strokeColor: color,
        strokeOpacity: defaultOpacity
      });
    } else {
      // Create new polyline
      const polyline = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: defaultOpacity,
        strokeWeight: 4,
        zIndex: 500
      });
      polyline.setMap(map);
      polylinesRef.current.set(key, polyline);
    }
  }, [getLegColor, map, pairKey, isLegVisible]);

  // Decode polyline string to LatLng array
  // Note: Google's decodePath can handle concatenated step polylines if they form a continuous path
  const decodePolyline = useCallback((encoded: string): google.maps.LatLng[] => {
    if (!encoded) {
      console.warn('decodePolyline: No encoded string provided');
      return [];
    }
    
    if (!(google.maps as any).geometry?.encoding) {
      console.error('decodePolyline: Google Maps geometry library not loaded');
      return [];
    }
    
    try {
      const decoded = (google.maps as any).geometry.encoding.decodePath(encoded);
      if (!decoded || decoded.length === 0) {
        console.warn('decodePolyline: Decoded path is empty');
        return [];
      }
      console.log(`decodePolyline: Decoded ${decoded.length} points from polyline length ${encoded.length}`);
      return decoded;
    } catch (err) {
      console.error('Error decoding polyline:', err, 'Encoded length:', encoded.length);
      return [];
    }
  }, []);

  // Draw legs from backend API response
  const drawLegsFromBackendResponse = useCallback((legs: Array<{ polyline: string }>, batchItems: ItineraryItem[]) => {
    if (!legs || legs.length === 0) return;
    legs.forEach((leg, i) => {
      if (i >= batchItems.length - 1) return; // Safety check
      const fromItem = batchItems[i];
      const toItem = batchItems[i + 1];
      if (leg.polyline) {
        const path = decodePolyline(leg.polyline);
        if (path.length > 0) {
          drawPolylineForLeg(path, fromItem.itemId, toItem.itemId);
        }
      }
    });
  }, [drawPolylineForLeg, decodePolyline]);

  // Route a single pair of items using backend API
  const routeSinglePair = useCallback(async (fromItem: ItineraryItem, toItem: ItineraryItem) => {
    try {
      const result = await TripPlannerAPI.getRoute(
        { lat: fromItem.location.lat, lon: fromItem.location.lon },
        { lat: toItem.location.lat, lon: toItem.location.lon }
      );
      
      if (result.success && result.legs && result.legs.length > 0 && result.legs[0].polyline) {
        const path = decodePolyline(result.legs[0].polyline);
        if (path.length > 0) {
          drawPolylineForLeg(path, fromItem.itemId, toItem.itemId);
        }
      }
    } catch (err: any) {
      // Skip this pair if routing fails (ZERO_RESULTS or other errors)
      console.warn(`Skipping route between ${fromItem.placeName} and ${toItem.placeName}:`, err?.message || err);
    }
  }, [drawPolylineForLeg, decodePolyline]);

  // Request a route for a batch of ordered items using backend API
  const routeBatch = useCallback(async (batchItems: ItineraryItem[]) => {
    try {
      if (!batchItems || batchItems.length < 2) return;
      
      // If only 2 items, route as a single pair
      if (batchItems.length === 2) {
        console.log('🔵 MapView: Routing single pair:', batchItems[0].placeName, '→', batchItems[1].placeName);
        await routeSinglePair(batchItems[0], batchItems[1]);
        return;
      }
      
      const origin = { lat: batchItems[0].location.lat, lon: batchItems[0].location.lon };
      const destination = { lat: batchItems[batchItems.length - 1].location.lat, lon: batchItems[batchItems.length - 1].location.lon };
      const waypoints = batchItems.slice(1, batchItems.length - 1).map(it => ({ lat: it.location.lat, lon: it.location.lon }));

      console.log('🔵 MapView: Calling Directions API for batch:', {
        origin: batchItems[0].placeName,
        destination: batchItems[batchItems.length - 1].placeName,
        waypointsCount: waypoints.length,
        totalPoints: batchItems.length
      });

      const result = await TripPlannerAPI.getRoute(origin, destination, waypoints);
      
      if (result.success && result.legs) {
        drawLegsFromBackendResponse(result.legs, batchItems);
      }
    } catch (err: any) {
      // If batch fails (especially ZERO_RESULTS), fall back to routing pairs individually
      const errorMessage = err?.message || '';
      const isZeroResults = errorMessage.includes('ZERO_RESULTS') || errorMessage.includes('No route found');
      
      if (isZeroResults && batchItems.length > 2) {
        console.warn('Batch routing failed with ZERO_RESULTS, falling back to individual pair routing');
        // Route each pair individually, skipping ones that fail
        for (let i = 0; i < batchItems.length - 1; i++) {
          // eslint-disable-next-line no-await-in-loop
          await routeSinglePair(batchItems[i], batchItems[i + 1]);
        }
      } else {
        console.error('Directions routing failed for batch:', err);
      }
    }
  }, [routeSinglePair, drawLegsFromBackendResponse]);

  // Calculate routes ONLY once when trip is loaded - not on scroll or other interactions
  useEffect(() => {
    if (!map || !items || items.length === 0) {
      console.log('🔄 MapView routing: Map or items not ready yet');
      return;
    }

    // Only calculate routes once - use ref to prevent recalculation
    if (routesCalculatedRef.current) {
      console.log('🔄 MapView routing: Routes already calculated, skipping');
      return;
    }

    const currentIds = items.map(it => it.itemId);
    console.log('🔄 MapView routing effect triggered (first time):', {
      itemsCount: items.length,
      currentIds: currentIds.slice(0, 5),
      mapReady: !!map
    });

    // Mark as calculated immediately to prevent duplicate calls
    routesCalculatedRef.current = true;

    // If not enough points, clear and bail
    if (currentIds.length < 2) {
      console.log('🔄 MapView routing: Not enough points (< 2), clearing polylines');
      clearAllPolylines();
      prevItemIdsRef.current = currentIds;
      return;
    }

    const maxPointsPerRequest = 25; // origin + destination + up to 23 waypoints

    const doInitialFullRoute = async () => {
      console.log('🗺️ MapView: Starting initial full route calculation for', items.length, 'items');
      clearAllPolylines();
      // Chunk across the items to respect limit; batches overlap last point
      let start = 0;
      while (start < items.length - 1) {
        const end = Math.min(start + maxPointsPerRequest - 1, items.length - 1);
        const slice = items.slice(start, end + 1);
        console.log(`🗺️ MapView: Routing batch ${start} to ${end} (${slice.length} items)`);
        // eslint-disable-next-line no-await-in-loop
        await routeBatch(slice);
        start = end; // overlap on last point
      }
      console.log('🗺️ MapView: Finished initial full route calculation');
      prevItemIdsRef.current = currentIds;
    };

    doInitialFullRoute();

    // Cleanup on unmount
    return () => {
      // Reset when component unmounts so routes can be recalculated if needed
      routesCalculatedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]); // ONLY depend on map - routes calculated once when map is ready (items captured in closure)

  // Update polyline opacities when selected item changes
  useEffect(() => {
    if (polylinesRef.current.size > 0) {
      updateAllPolylineOpacities();
    }
  }, [selectedItemId, updateAllPolylineOpacities]);
  
  // Helper to get color for an item
  const getItemColor = useCallback((itemId: string): string => {
    // Always return the original day color
    const dayId = itemToDayMap[itemId];
    if (dayId && dayColorMap[dayId]) {
      return dayColorMap[dayId];
    }
    
    return '#3B82F6'; // Default blue
  }, [itemToDayMap, dayColorMap]);

  // Helper to determine if an item should be transparent (mobile only)
  const getItemOpacity = useCallback((itemId: string): number => {
    // If the marker itself is selected, always fully opaque
    if (itemId === selectedItemId) {
      return 1.0;
    }
    
    if (!isMobile || !selectedDayId) {
      return 1.0; // Fully opaque on desktop or when no day is selected
    }
    
    const itemDayId = itemToDayMap[itemId];
    // Selected day items are fully opaque, others are less transparent
    return itemDayId === selectedDayId ? 1.0 : 0.7;
  }, [isMobile, selectedDayId, itemToDayMap, selectedItemId]);
  
  // Get the center location - prioritize selected item, fallback to first item, then default
  const getCenterLocation = () => {
    if (centerOnItemId && items.length > 0) {
      const selectedItem = items.find(item => item.itemId === centerOnItemId);
      if (selectedItem) {
        if (selectedItem.location) {
          return { lat: selectedItem.location.lat, lng: selectedItem.location.lon };
        }
      }
    }
    
    if (items[0]) {
      if (items[0].location) {
        return { lat: items[0].location.lat, lng: items[0].location.lon };
      }
    }
    
    return { lat: 35.6762, lng: 139.6503 }; // Default to Tokyo
  };
  
  const centerLocation = getCenterLocation();
  
  // Handler for when user location is found
  const handleLocationFound = useCallback((location: { lat: number; lng: number }) => {
    setCurrentLocation(location);
  }, []);
  
  // Helper function to create marker icon based on zoom level
  const createMarkerIcon = useCallback((
    itemNumber: number,
    placeName: string,
    isSelected: boolean,
    zoom: number,
    itemId: string,
    opacity: number = 1.0
  ) => {
    // Zoom levels (adjusted to show labels earlier):
    // < 10: Small pin markers (no numbers)
    // 10-11: Medium pins with numbers
    // >= 12: Pins with text labels (visible earlier when zoomed out)
    
    // Get the color for this item
    const color = getItemColor(itemId);
    
    // Apply opacity to color
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(color);
    const colorWithOpacity = rgb 
      ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
      : `rgba(59, 130, 246, ${opacity})`; // Default blue with opacity
    
    // Use bold black stroke when selected, otherwise use darker version of color
    const strokeColor = isSelected 
      ? '#000000' // Bold black stroke for selected markers
      : (() => {
          const rgb = hexToRgb(color);
          return rgb 
            ? `rgba(${Math.max(0, rgb.r - 20)}, ${Math.max(0, rgb.g - 20)}, ${Math.max(0, rgb.b - 20)}, ${opacity})`
            : `rgba(37, 99, 235, ${opacity})`;
        })();
    
    // Stroke width - thicker when selected
    const strokeWidth = isSelected ? 3 : 1.5;
    
    if (zoom < 10) {
      // Small pin marker for zoomed out view (no number)
      const pinWidth = isSelected ? 20 : 16;
      const pinHeight = isSelected ? 28 : 22;
      
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="${pinWidth}" height="${pinHeight}" viewBox="0 0 ${pinWidth} ${pinHeight}" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow-${itemNumber}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/>
              </filter>
            </defs>
            <!-- Small teardrop pin shape -->
            <path d="M ${pinWidth/2} ${pinHeight-2} 
                     C ${pinWidth/2} ${pinHeight-2}, ${pinWidth*0.85} ${pinHeight*0.55}, ${pinWidth*0.85} ${pinHeight*0.35}
                     A ${pinWidth*0.35} ${pinWidth*0.35} 0 1 0 ${pinWidth*0.15} ${pinHeight*0.35}
                     C ${pinWidth*0.15} ${pinHeight*0.55}, ${pinWidth/2} ${pinHeight-2}, ${pinWidth/2} ${pinHeight-2} Z" 
                  fill="${colorWithOpacity}" stroke="${strokeColor}" stroke-width="${strokeWidth}" filter="url(#shadow-${itemNumber})"/>
          </svg>
        `),
        scaledSize: { width: pinWidth, height: pinHeight },
        anchor: { x: pinWidth / 2, y: pinHeight - 2 }
      } as any;
    } else if (zoom < 12) {
      // Medium pin with number
      const pinWidth = isSelected ? 36 : 28;
      const pinHeight = isSelected ? 48 : 38;
      const numberSize = isSelected ? 16 : 14;
      
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="${pinWidth}" height="${pinHeight}" viewBox="0 0 ${pinWidth} ${pinHeight}" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow-${itemNumber}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/>
              </filter>
            </defs>
            <!-- Teardrop pin shape -->
            <path d="M ${pinWidth/2} ${pinHeight-2} 
                     C ${pinWidth/2} ${pinHeight-2}, ${pinWidth*0.85} ${pinHeight*0.55}, ${pinWidth*0.85} ${pinHeight*0.35}
                     A ${pinWidth*0.35} ${pinWidth*0.35} 0 1 0 ${pinWidth*0.15} ${pinHeight*0.35}
                     C ${pinWidth*0.15} ${pinHeight*0.55}, ${pinWidth/2} ${pinHeight-2}, ${pinWidth/2} ${pinHeight-2} Z" 
                  fill="${colorWithOpacity}" stroke="${strokeColor}" stroke-width="${strokeWidth}" filter="url(#shadow-${itemNumber})"/>
            <!-- White circle for number -->
            <circle cx="${pinWidth/2}" cy="${pinHeight*0.35}" r="${pinWidth*0.25}" fill="white" opacity="${opacity}"/>
            <!-- Number text -->
            <text x="${pinWidth/2}" y="${pinHeight*0.35 + numberSize*0.35}" text-anchor="middle" 
                  fill="${colorWithOpacity}" font-size="${numberSize}" font-weight="bold" font-family="Arial, sans-serif" opacity="${opacity}">${itemNumber}</text>
          </svg>
        `),
        scaledSize: { width: pinWidth, height: pinHeight },
        anchor: { x: pinWidth / 2, y: pinHeight - 2 }
      } as any;
    } else {
      // Large pin with text label next to it (Google Maps style) visible from earlier zoom
      const pinWidth = isSelected ? 36 : 30;
      const pinHeight = isSelected ? 48 : 40;
      const numberSize = isSelected ? 16 : 14;
      
      // Use white with partial transparency for label background
      const labelBgColor = `rgba(255, 255, 255, ${0.85 * opacity})`; // White with 85% opacity adjusted by marker opacity
      const labelTextColor = colorWithOpacity; // Use day color for text with opacity
      
      // Calculate label dimensions with word wrapping
      const labelText = placeName || '';
      const maxCharsPerLine = 16; // approximate characters per line
      const words = labelText.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      for (const word of words) {
        if ((currentLine + (currentLine ? ' ' : '') + word).length <= maxCharsPerLine) {
          currentLine = currentLine ? currentLine + ' ' + word : word;
        } else {
          if (currentLine) lines.push(currentLine);
          // If a single word is too long, hard-break it
          if (word.length > maxCharsPerLine) {
            let start = 0;
            while (start < word.length) {
              lines.push(word.substring(start, Math.min(start + maxCharsPerLine, word.length)));
              start += maxCharsPerLine;
            }
            currentLine = '';
          } else {
            currentLine = word;
          }
        }
      }
      if (currentLine) lines.push(currentLine);

      const charWidth = 7; // px per character approx
      const horizontalPadding = 12;
      const verticalPadding = 8;
      const lineHeight = 14; // px between baselines
      const contentWidth = Math.min(Math.max(...lines.map(l => l.length), 1) * charWidth, 260);
      const labelWidth = contentWidth + horizontalPadding * 2;
      const labelHeight = lines.length * lineHeight + verticalPadding * 2;
      const labelX = pinWidth + 4; // Position label to the right of pin
      const labelY = Math.max(4, pinHeight * 0.25); // Align with top of pin
      
      const totalWidth = pinWidth + labelWidth + 8;
      const totalHeight = Math.max(pinHeight, labelHeight + labelY);

      // Build multi-line text using tspans (no truncation)
      const tspans = lines.map((line, idx) => {
        // First line baseline offset from labelY
        const baselineY = labelY + verticalPadding + 11; // approx baseline adjustment
        return `<tspan x="${labelX + horizontalPadding}" y="${baselineY + idx * lineHeight}">${line}</tspan>`;
      }).join('');
      
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="pinShadow-${itemNumber}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.4)"/>
              </filter>
              <filter id="labelShadow-${itemNumber}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.2)"/>
              </filter>
            </defs>
            
            <!-- Label background with rounded rectangle -->
            <rect x="${labelX}" y="${labelY}" width="${labelWidth}" height="${labelHeight}" 
                  rx="12" fill="${labelBgColor}" stroke="${isSelected ? '#000000' : strokeColor}" stroke-width="${isSelected ? 2 : 1.5}" 
                  filter="url(#labelShadow-${itemNumber})"/>
            
            <!-- Label text (wrapped) -->
            <text fill="${labelTextColor}" font-size="12" font-weight="600" font-family="Arial, sans-serif" opacity="${opacity}">
              ${tspans}
            </text>
            
            <!-- Teardrop pin shape -->
            <path d="M ${pinWidth/2} ${pinHeight-2} 
                     C ${pinWidth/2} ${pinHeight-2}, ${pinWidth*0.85} ${pinHeight*0.55}, ${pinWidth*0.85} ${pinHeight*0.35}
                     A ${pinWidth*0.35} ${pinWidth*0.35} 0 1 0 ${pinWidth*0.15} ${pinHeight*0.35}
                     C ${pinWidth*0.15} ${pinHeight*0.55}, ${pinWidth/2} ${pinHeight-2}, ${pinWidth/2} ${pinHeight-2} Z" 
                  fill="${colorWithOpacity}" stroke="${strokeColor}" stroke-width="${strokeWidth}" 
                  filter="url(#pinShadow-${itemNumber})"/>
            
            <!-- White circle for number -->
            <circle cx="${pinWidth/2}" cy="${pinHeight*0.35}" r="${pinWidth*0.25}" fill="white" opacity="${opacity}"/>
            
            <!-- Number text -->
            <text x="${pinWidth/2}" y="${pinHeight*0.35 + numberSize*0.35}" text-anchor="middle" 
                  fill="${colorWithOpacity}" font-size="${numberSize}" font-weight="bold" font-family="Arial, sans-serif" opacity="${opacity}">${itemNumber}</text>
          </svg>
        `),
        scaledSize: { width: totalWidth, height: totalHeight },
        anchor: { x: pinWidth / 2, y: pinHeight - 2 }
      } as any;
    }
  }, [getItemColor]);
  
  // Effect to track zoom changes
  useEffect(() => {
    if (!map) return;
    
    const zoomListener = map.addListener('zoom_changed', () => {
      const zoom = map.getZoom();
      if (zoom !== undefined) {
        setCurrentZoom(zoom);
      }
    });
    
    // Set initial zoom
    const initialZoom = map.getZoom();
    if (initialZoom !== undefined) {
      setCurrentZoom(initialZoom);
    }
    
    return () => {
      google.maps.event.removeListener(zoomListener);
    };
  }, [map]);

  // Effect to handle centering when centerOnItemId changes
  useEffect(() => {
    // Only center if the click did NOT come from a marker
    if (centerOnItemId && items.length > 0 && !clickFromMarkerRef.current) {
      const targetItem = items.find(item => item.itemId === centerOnItemId);
      if (targetItem && map) {
        // Get the location for the target item
        const position = new google.maps.LatLng(targetItem.location.lat, targetItem.location.lon);
        
        // Check if the location is already in the viewport
        const bounds = map.getBounds();
        const isInViewport = bounds && bounds.contains(position);
        
        // Only center if the location is not in the viewport
        if (!isInViewport) {
          // Center the map on the target item (without zooming)
          map.setCenter(position);
          
          // Show centering indicator
          setIsCentering(true);
          
          // Hide centering indicator after a short delay
          setTimeout(() => setIsCentering(false), 1000);
        }
      }
    }
    
    // Reset the flag after processing
    clickFromMarkerRef.current = false;
  }, [centerOnItemId, items, map]);

  // Effect to prevent map type control dropdown from opening
  useEffect(() => {
    if (!map) return;

    const preventDropdown = () => {
      // Find all map type control buttons and prevent dropdown
      const mapTypeButtons = document.querySelectorAll('.gm-style .gm-style-mtc button');
      mapTypeButtons.forEach((button) => {
        // Remove existing click listeners and add our own
        const newButton = button.cloneNode(true) as HTMLElement;
        button.parentNode?.replaceChild(newButton, button);
        
        newButton.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          
          // Toggle between roadmap and satellite directly
          const currentMapType = map.getMapTypeId();
          if (currentMapType === google.maps.MapTypeId.ROADMAP) {
            map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
          } else {
            map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
          }
        });
      });
    };

    // Run after a short delay to ensure controls are rendered
    const timer = setTimeout(preventDropdown, 500);
    
    // Also run when map is idle (controls are fully loaded)
    const idleListener = map.addListener('idle', () => {
      preventDropdown();
    });

    return () => {
      clearTimeout(timer);
      google.maps.event.removeListener(idleListener);
    };
  }, [map]);

  if (!items || items.length === 0) {
    return (
      <div className="bg-gray-200 h-96 w-full rounded-lg flex items-center justify-center border border-gray-300">
        <div className="text-center">
          <p className="text-gray-500 mb-2">No locations to display</p>
          <p className="text-xs text-gray-400">
            Items count: {items ? items.length : 'undefined'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-center py-2">
      <div className="w-full h-[calc(100%-1rem)] rounded-2xl overflow-hidden shadow-xl border border-gray-200 relative bg-white">
        {/* Custom styles for Google Maps controls */}
        <style>{`
          /* Ensure Google Maps controls are above the background overlay */
          .gm-style .gm-style-mtc,
          .gm-style .gm-zoom-control,
          .gm-style .gm-style-cc,
          .gm-style .gm-svpc,
          .gm-style .gm-rotate-control {
            z-index: 100 !important;
            position: relative !important;
          }
          
          /* Map type control (Map/Satellite buttons) */
          .gm-style .gm-style-mtc {
            background-color: rgba(255, 255, 255, 0.95) !important;
            border-radius: 6px !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
            padding: 2px !important;
          }
          
          /* Map type control buttons */
          .gm-style .gm-style-mtc button {
            background-color: transparent !important;
            color: #5f6368 !important;
          }
          
          .gm-style .gm-style-mtc button:hover {
            background-color: rgba(0, 0, 0, 0.05) !important;
          }
          
          /* Hide the dropdown menu for map type control */
          .gm-style .gm-style-mtc div[role="menu"],
          .gm-style .gm-style-mtc .gm-style-mtc-bbw,
          .gm-style .gm-style-mtc .gm-style-mtc-bb {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
          
          /* Prevent dropdown from opening */
          .gm-style .gm-style-mtc button[aria-expanded="true"] {
            pointer-events: none !important;
          }
          
          /* Zoom controls */
          .gm-style .gm-zoom-control {
            background-color: rgba(255, 255, 255, 0.95) !important;
            border-radius: 6px !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          
          /* Scale control */
          .gm-style .gm-style-cc {
            background-color: rgba(255, 255, 255, 0.9) !important;
            border-radius: 4px !important;
            padding: 4px 8px !important;
          }
          
          /* Street view control */
          .gm-style .gm-svpc {
            background-color: rgba(255, 255, 255, 0.95) !important;
            border-radius: 6px !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          
          /* Rotate control */
          .gm-style .gm-rotate-control {
            background-color: rgba(255, 255, 255, 0.95) !important;
            border-radius: 6px !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
        `}</style>
        {/* DateScroller for mobile - positioned below Exit Map button */}
        {isMobile && days && days.length > 0 && onDateSelect && (
          <div className="absolute top-14 left-0 right-0 z-50">
            <DateScroller
              days={days.map(day => ({ dayId: day.dayId, dayDate: day.dayDate, title: day.title, color: day.color }))}
              selectedDayId={selectedDayId || null}
              onDateSelect={onDateSelect}
              noCurve={true}
              useDayColors={true}
              transparent={true}
            />
          </div>
        )}
        {/* My Location button */}
        <MyLocationButton onLocationFound={handleLocationFound} />
        {/* Centering indicator */}
        {isCentering && (
          <div className="absolute top-3 left-3 z-10 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg animate-pulse">
            🗺️ Centering map...
          </div>
        )}
        
        {/* No client-side geocoding; backend provides coordinates */}
        
        
        
        <GoogleMap
          style={{ width: '100%', height: '100%', minHeight: '100%' }}
          defaultCenter={centerLocation}
          defaultZoom={14}
          mapTypeId="roadmap"
          zoomControl={true}
          mapTypeControl={true}
          scaleControl={true}
          streetViewControl={true}
          rotateControl={true}
          fullscreenControl={false}
          clickableIcons={true}
          gestureHandling="greedy"
          disableDefaultUI={false}
          onClick={() => {
            // Map click - don't center on any item
            clickFromMarkerRef.current = false;
          }}
        >
          {/* Render all markers - ensure all are clickable */}
          {items.map((item) => {
            const position = { lat: item.location.lat, lng: item.location.lon };
            const isSelected = item.itemId === selectedItemId;
            
            // Get the original index from the items array to maintain consistent numbering
            const originalIndex = items.findIndex(originalItem => originalItem.itemId === item.itemId);
            
            const itemOpacity = getItemOpacity(item.itemId);
            const markerIcon = createMarkerIcon(
              originalIndex + 1,
              item.placeName,
              isSelected,
              currentZoom,
              item.itemId,
              itemOpacity
            );
            
            // Higher z-index for selected marker to ensure it's on top and clickable
            const markerZIndex = isSelected ? 1000 : 100;
            
            return (
              <Marker
                key={`${item.itemId}-${originalIndex}`}
                position={position}
                title={`${item.name} - ${item.placeName}${isSelected ? ' (Selected)' : ''}`}
                onClick={(e) => {
                  // Prevent event from bubbling to map if stop method exists
                  if (e && typeof (e as any).stop === 'function') {
                    (e as any).stop();
                  }
                  clickFromMarkerRef.current = true;
                  console.log('📍 Marker clicked:', item.itemId, 'isSelected:', isSelected, 'Calling onItemSelect:', !!onItemSelect);
                  if (onItemSelect) {
                    onItemSelect(item.itemId);
                  }
                }}
                zIndex={markerZIndex}
                icon={markerIcon}
                optimized={false} // Disable optimization to ensure all markers are always interactive
                clickable={true} // Explicitly enable clicking
              />
            );
          })}
          
          {/* Current Location Marker */}
          {currentLocation && (
            <Marker
              position={currentLocation}
              title="Your Location"
              zIndex={2000}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="currentLocationShadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.4)"/>
                      </filter>
                    </defs>
                    <!-- Outer pulse ring -->
                    <circle cx="16" cy="16" r="15" fill="#3B82F6" opacity="0.2"/>
                    <!-- Middle ring -->
                    <circle cx="16" cy="16" r="11" fill="#3B82F6" opacity="0.4"/>
                    <!-- Inner blue circle -->
                    <circle cx="16" cy="16" r="7" fill="#3B82F6" stroke="white" stroke-width="2" filter="url(#currentLocationShadow)"/>
                    <!-- Center white dot -->
                    <circle cx="16" cy="16" r="3" fill="white"/>
                  </svg>
                `),
                scaledSize: { width: 32, height: 32 },
                anchor: { x: 16, y: 16 }
              } as any}
            />
          )}
            </GoogleMap>
          </div>
        </div>
      );
    };

const MapView: React.FC<MapViewProps> = ({ items, selectedItemId, onItemSelect, centerOnItemId, days, selectedDayId, onDateSelect, isMobile }) => {
  // Get Google Maps API key from environment variables
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  

  


  if (!googleMapsApiKey || googleMapsApiKey === 'your_google_maps_api_key_here') {
    return (
      <div className="bg-gray-200 h-96 w-full rounded-lg flex items-center justify-center border border-gray-300">
        <div className="text-center max-w-md mx-auto p-4">
          <div className="text-red-500 text-4xl mb-2">⚠️</div>
          <p className="text-gray-600 mb-2 font-semibold">Google Maps API key not configured</p>
          <p className="text-sm text-gray-500 mb-3">
            To enable the map feature, you need to set up a Google Maps API key.
          </p>
          <div className="text-left text-xs text-gray-600 bg-gray-100 p-3 rounded-lg mb-3">
            <p className="font-semibold mb-1">Setup steps:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
              <li>Enable Maps JavaScript API and Places API</li>
              <li>Create an API key</li>
              <li>Create a <code className="bg-gray-200 px-1 rounded">.env</code> file in the frontend directory</li>
              <li>Add: <code className="bg-gray-200 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY=your_key_here</code></li>
            </ol>
          </div>
          <p className="text-xs text-gray-400">
            Current value: {googleMapsApiKey || 'undefined'}
          </p>
        </div>
      </div>
    );
  }

  // Debug: Log the current state
  console.log('MapView Debug:', {
    googleMapsApiKey: googleMapsApiKey ? 'Present' : 'Missing',
    itemsCount: items.length
  });

  if (!googleMapsApiKey || googleMapsApiKey === 'your_google_maps_api_key_here') {
    return (
      <div className="bg-gray-200 h-96 w-full rounded-lg flex items-center justify-center border border-gray-300">
        <div className="text-center max-w-md mx-auto p-4">
          <div className="text-red-500 text-4xl mb-2">⚠️</div>
          <p className="text-gray-600 mb-2 font-semibold">Google Maps API key not configured</p>
          <p className="text-sm text-gray-500 mb-3">
            To enable the map feature, you need to set up a Google Maps API key.
          </p>
          <div className="text-left text-xs text-gray-600 bg-gray-100 p-3 rounded-lg mb-3">
            <p className="font-semibold mb-1">Setup steps:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
              <li>Enable Maps JavaScript API and Places API</li>
              <li>Create an API key</li>
              <li>Create a <code className="bg-gray-200 px-1 rounded">.env</code> file in the frontend directory</li>
              <li>Add: <code className="bg-gray-200 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY=your_key_here</code></li>
            </ol>
          </div>
          <p className="text-xs text-gray-400">
            Current value: {googleMapsApiKey || 'undefined'}
          </p>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="bg-gray-200 h-96 w-full rounded-lg flex items-center justify-center border border-gray-300">
        <div className="text-center">
          <p className="text-gray-500 mb-2">No locations to display</p>
          <p className="text-xs text-gray-400">
            Items count: {items ? items.length : 'undefined'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={googleMapsApiKey} libraries={['geometry']}>
      <MapContent 
        items={items}
        selectedItemId={selectedItemId}
        onItemSelect={onItemSelect}
        centerOnItemId={centerOnItemId}
        days={days}
        selectedDayId={selectedDayId}
        onDateSelect={onDateSelect}
        isMobile={isMobile}
      />
    </APIProvider>
  );
};

export default MapView;
